import { Component, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { DemographicInfo } from 'src/app/people/models/demographicInfo';
import { AddressService, IAddressValidationResult } from '../../services/address.service';
import { DialogDirective } from 'src/shared-app-portal/components/dialog/dialog.directive';
import { AuthService } from 'src/app/auth/auth.service';
import { AddressHelper } from '../../services/address-helper.service';
import { AddressWarningDialogComponent } from 'src/portal/dialogs/address-warning.dialog/address-warning.dialog.component';
import { TeamCredentialAboutModel } from 'src/app/teams/team-about/team-credential-about.model';
import { CredentialSingletonService } from '../../../app/shared/services/credential-singleton.service';
import { IamPersonOrTeam } from 'src/app/people/models/IHaveAttributes';

@Component({
  selector: 'address',
  templateUrl: './address.component.html',
  styleUrls: ['./address.component.scss'],
  standalone: false
})
export class AddressComponent {
  @Input()
  addressHelper: AddressHelper;

  @Input()
  personOrTeam: IamPersonOrTeam;

  @Input()
  teamCredential: TeamCredentialAboutModel;

  @Output()
  addressUpdated = new EventEmitter(); //emits addressValidationCacheKey

  ///used on the warning modal
  validationResult: IAddressValidationResult;

  get isValid(): boolean {
    return this.addressHelper?.valid;
  }

  @Input()
  validateAddress: boolean = false;

  @Input()
  staffCanValidate: boolean = false;

  get mode(): 'addModal' | 'editModal' | 'inline' {
    if (!this.validateAddress && !this.staffCanValidate) {
      return 'inline';
    }
    if (this.hasAddress) {
      return 'editModal';
    }
    return 'addModal';
  }
  private get isValidating() {
    if (this.staffCanValidate) {
      if (!this.isStaff) {
        return false; //admin section, but non-staff
      }
    } else if (!this.validateAddress) {
      return false;
    }
    return true;
  }

  @ViewChild('addressDialog', { static: true })
  addressDialog: DialogDirective;

  @ViewChild(AddressWarningDialogComponent, { static: true })
  addressWarning: AddressWarningDialogComponent;
  isStaff: boolean;

  constructor(
    private addressService: AddressService,
    authService: AuthService,
    private credentialSingletonService: CredentialSingletonService
  ) {
    this.isStaff = authService.staffLevelAccess;
  }

  //Set when this.updateDemographicInfo(DemographicInfo) is called
  correctedAddress: DemographicInfo;

  public saveWithoutValidate = (clickClose) => {
    clickClose(null); //close the modal
    this.updateModelAndPersist(null);
  };

  //called when there is a corrected address
  public useAddress = async (correctedAddress: DemographicInfo) => {
    if (correctedAddress) {
      const model = this.addressHelper.updateDemographicInfo(correctedAddress); //update the read-only address and all controls

      const validationResult = await this.addressService.validate(
        model,
        this.addressHelper.addressValidationCacheKey
      );
      await this.updateModelAndPersist(validationResult);
    }
  };

  public openWarningModal = (validationResult: IAddressValidationResult) => {
    //set inputs used on the modal
    this.validationResult = validationResult;
    if (validationResult.correctedAddress) {
      this.correctedAddress = this.addressHelper
        .buildDemographicInfo(validationResult.correctedAddress)
        .updateValidationStatus('Success');
    }

    //may or may not have a corrected address
    this.addressWarning
      .open(this.correctedAddress, this.addressHelper?.address, validationResult.message)
      .then((result) => {
        if (result === false) {
          //clicked 'Edit Address'
          this.openAddressModal(); //clicked 'Edit Address'. re-open modal
        }
      });
  };

  public openAddressModal = async () => {
    this.correctedAddress = null;
    //promise is wired up in DialogDirective.open which listens to afterClosed dialogRef.observable
    await this.addressDialog.open();
  };

  onOkay: () => Promise<boolean> = async () => {
    if (!this.isValidating) {
      await this.updateModelAndPersist(null); //emit address updated without validation
      return true;
    }

    const validationResult = await this.addressService.validate(
      this.addressHelper.getModelFromForm(),
      this.addressHelper.addressValidationCacheKey
    );

    if (!validationResult) {
      return false; //probably 400/500 error on validation.
      //addressService.validate should have shown toast error
      //returning false will keep address modal open
    }
    await this.updateModelAndPersist(validationResult);

    if (validationResult.status !== 'Success') {
      this.openWarningModal(validationResult);
    }
    return true;
  };

  cancelEditAddress = () => {
    this.addressHelper.updateDemographicInfo(this.addressHelper.address); //edit was cancelled. Revert Form
  };

  private updateModelAndPersist = async (validationResult: IAddressValidationResult = null) => {
    const model = this.addressHelper.copyFormToModel(validationResult);

    /** Persists address via API or local storage */
    const isValidated = !!validationResult;
    this.addressHelper.isInvalid = false; //clear Invalid status whenever address is updated
    if (!this.personOrTeam) {
      this.addressUpdated.emit(); //tells application to update local storage with updated address
      return;
    }

    await this.credentialSingletonService
      .saveAddress(model, isValidated ? this.addressHelper.addressValidationCacheKey : '')
      .then((demographicInfo) => {
        this.personOrTeam.demographicInfo = demographicInfo;
        this.addressHelper.updateDemographicInfo(demographicInfo); //update read-only and form to whatever the API responded with
      });
  };

  get hasAddress(): boolean {
    return this.addressHelper?.hasAddress;
  }

  async clearAddress() {
    this.addressHelper.updateDemographicInfo(new DemographicInfo()); //clear the form
    await this.updateModelAndPersist(null); //emit the cleared address with no validation
  }
}
